import { Box, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useTranslation } from "react-i18next";
import { useTheme } from "../hooks";

// The two ways an entry point fails to resolve. They are kept apart because
// they are different answers to a payer: an expired link is something they can
// act on by asking for a new one, a page that never existed is not.
export type NotFoundVariant = "not-found" | "expired" | "unavailable";

const NotFound = ({
  variant = "not-found",
}: {
  variant?: NotFoundVariant;
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const key =
    variant === "expired"
      ? "portal.expired"
      : variant === "unavailable"
      ? "portal.unavailable"
      : "portal.not_found";

  return (
    <Box
      role="status"
      sx={{
        maxWidth: 560,
        mx: "auto",
        my: 8,
        px: 3,
        py: 5,
        textAlign: "center",
        borderRadius: 2,
        // The theme always resolves to something -- the neutral default when
        // no tenant does -- so the shell is branded rather than blank.
        bgcolor: theme?.light_gray,
        borderTop: 4,
        borderColor: theme?.color_primary,
      }}
    >
      {/* Both states are failures, and the copy alone reads like an ordinary
          page until it is finished. The icon says so before it is read.
          `color="error"` comes from the palette rather than a hex literal.
          Decorative on purpose: the heading beside it carries the same
          meaning, and the surrounding role="status" announces both. */}
      <ErrorOutlineIcon color="error" aria-hidden sx={{ fontSize: 48, mb: 1 }} />
      <Typography variant="h5" fontWeight="bold" mb={1.5}>
        {t(`${key}.title`)}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {t(`${key}.body`)}
      </Typography>
    </Box>
  );
};

export default NotFound;
